import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  CustomSelect,
  DangerAlert,
  Form,
  FormItem,
  FormRow,
  InfoAlert,
  Input,
  SecondaryBtn,
  Textarea,
  WaitingAlert,
  SuccessAlert,
  RadioGroup,
  CtaLink,
} from '@tmapy/style-guide';

import type {
  NestedGeodesistResponse,
  PatchedBBDocUpdateRequest,
  UserListResponse,
} from '../../openapi/types';
import { PrejimkaTypesEnum } from '../../openapi/types/PrejimkaTypesEnum';
import {
  DocsRetrieveResponse,
  useDocsRetrieveResource,
} from '../../openapi/operations/docsRetrieve';
import { DtmDataTypeEnum } from '../../openapi/types/DtmDataTypeEnum';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import { geodesistsList } from '../../openapi/operations/geodesistsList';
import { bbDocsImportDocCreate } from '../../openapi/operations/bbDocsImportDocCreate';
import { bbDocsPartialUpdate } from '../../openapi/operations/bbDocsPartialUpdate';
import { docsListOperation } from '../../openapi/operations/docsList';
import { bbDocsCreate } from '../../openapi/operations/bbDocsCreate';
import { bbDocsDestroy } from '../../openapi/operations/bbDocsDestroy';
import { bbDocsClosePartialUpdate } from '../../openapi/operations/bbDocsClosePartialUpdate';
import { bbDocsSubmitPartialUpdate } from '../../openapi/operations/bbDocsSubmitPartialUpdate';
import { bbDocsAcceptPartialUpdate } from '../../openapi/operations/bbDocsAcceptPartialUpdate';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LogList } from '../../logs/components/LogList';
import { HistoryList } from '../../history/components/HistoryList';

import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAppContext,
  useAuth,
  useLogDownloadLink,
  useFormItemServerErrors,
  useHistoryAction,
  useInput,
  useSelect,
  useSelector,
  useSSE,
  useAction,
  toBtnQueryState,
} from '../../hooks';
import { QUERY_STATE } from '../../constants';
import { actionResourceInvalidate } from '../../redux/resource/actions';

import { DOC_STATE, DOCDETAIL_LOAD_SUCCESS, DOCDETAIL_LOAD_WAITING } from '../constants';
import { loadDoc, updateDoc } from '../actions/docDetail';
import { getFormValues } from '../utils/getformvalues';

import { docFormMessages, docTitleMessages, dtmDocumentationTypesMessages } from './docsMessages';
import { docEditButtonsMsg, docEditMessages, validatorMessages } from './docEditMessages';

import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { DocBadge } from './DocBadge';
import { DocDownloadFiles } from './DocDownloadFiles';

import './Doc.scss';

const renderUserSelectItem = (item: UserListResponse | NestedGeodesistResponse) => (
  <>
    {item.name} {item.surname}
  </>
);

export type DocBbEditContainerProps = {
  docId?: number;
};

export const DocBb: React.FC<DocBbEditContainerProps> = ({ docId }) => {
  useSSE(SSE_TYPE.DOCS, docId);

  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const { appType } = useAppContext();

  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [submitQueryState, setSubmitQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [acceptQueryState, setAcceptQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [importQueryState, setImportQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [closeQueryState, setCloseQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [destroyQueryState, setDestroyQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [validate, setValidate] = useState(false);
  const [importError, setImportError] = useState<any>(null);

  const config = useSelector((state) => state.app.config);
  const removeQueryState = useSelector((state) => state.doc.removeQueryState);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const auth = useAuth();

  const stateData = useSelector((state) => state.doc.data);
  const resource = useDocsRetrieveResource(docId ? { id: docId } : null);

  const data = useMemo(() => {
    const cachedData = stateData.id === docId ? stateData : resource.data;
    return docId && cachedData
      ? cachedData
      : {
          ...defaultValues,
          state: DOC_STATE.INPROGRESS,
          prejimka_type: PrejimkaTypesEnum.BB_IMPORT,
          flow: FlowEnum.BB_IMPORT,
        };
  }, [resource.data, stateData, docId]);

  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('BBDocs', 'partial_update', data),
      docsUpdate: auth.hasPermission('BBDocs', 'update', data),
      docsDestroy: auth.hasPermission('BBDocs', 'destroy', data),

      docsActionSubmit: auth.hasPermission('BBDocs', 'action.submit', data),
      docsActionAccept: auth.hasPermission('BBDocs', 'action.accept', data),
      docsActionImport: auth.hasPermission('BBDocs', 'action.import_doc', data),
      docsActionClose: auth.hasPermission('BBDocs', 'action.close', data),

      docsActionShowLog: auth.hasPermission('BBDocs', 'action.logs', data),
      docsActionMapEdit: auth.hasPermission('BBDocs', 'action.map_edit', data),

      //download section
      docsActionDownloadCloseLog: auth.hasPermission('BBDocs', 'download_close_log', data),
      docsActionDownloadImportLog: auth.hasPermission('BBDocs', 'download_import_log', data),
    }),
    [auth, data],
  );

  const [showLogDownloadError, setShowLogDownloadError] = useState(false);
  const handleShowDownloadLogError = (visible: boolean) => {
    setShowLogDownloadError(visible);
  };

  const closeLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_close_log',
    handleShowDownloadLogError,
  );

  const importLogDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_import_log',
    handleShowDownloadLogError,
  );

  const routeDetail = useHistoryAction('bbDocDetail', { id: docId! });
  const routeList = useHistoryAction('docList', { appType });

  useEffect(() => {
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
    }
  }, [resource.data, dispatch]);

  // ACTIONS
  const handleRefetch = useCallback(() => {
    resource.refetch();
  }, [resource]);

  const actionQuery = useMemo(() => ({ id: docId }), [docId]);
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: resource.refetch,
    routeList,
    listPath: docsListOperation().path,
  });

  const handleSaveDocWithValidation = async () => {
    setValidate(true);

    const isSomeFormItemInvalid = Object.values(formItems).some((formItem: any) => {
      return formItem.validator?.(formItem.value);
    });

    if (!isSomeFormItemInvalid) {
      return await handleSaveDocWithoutValidation();
    }
    return null;
  };

  const handleSaveDocWithoutValidation = async () => {
    const formValues = getFormValues(formItems);
    const values = {
      ...formValues,
      // select
      geodesist: formValues.geodesist.url || '',
    };

    return await handleSave(values);
  };

  const handleSave = useCallback(
    async (values: PatchedBBDocUpdateRequest) => {
      setSaveQueryState(QUERY_STATE.WAITING);
      let response;

      try {
        if (docId) {
          response = await bbDocsPartialUpdate({
            query: { id: docId },
            request: values,
          });

          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);
            dispatch(updateDoc(response.data));
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        } else {
          response = await bbDocsCreate({
            request: values,
          });
          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);

            routeDetail.replace({ id: response.data.id });
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        }
      } finally {
        const docsList = docsListOperation();
        dispatch(actionResourceInvalidate(docsList.path));
      }
      if (response.ok) {
        const attachments = response.data.attachments ?? [];
        return {
          download_attachments_link: '',
          ...response.data,
          attachments,
        };
      }
      return null;
    },
    [docId, routeDetail, appType, dispatch],
  );

  const handleSubmitBtnClick = () => {
    doAction(bbDocsSubmitPartialUpdate, setSubmitQueryState);
  };

  const handleAcceptBtnClick = () => {
    doAction(bbDocsAcceptPartialUpdate, setAcceptQueryState);
  };

  const handleDeleteBtnClick = () => {
    doAction(bbDocsDestroy, setDestroyQueryState, { refetch: false, redirectToList: true });
  };

  const handleCloseBtnClick = () => {
    doAction(bbDocsClosePartialUpdate, setCloseQueryState);
  };

  const handleImportBtnClick = async () => {
    const data = await handleSaveDocWithValidation();
    if (!data) return;

    setImportError(null);
    setImportQueryState(QUERY_STATE.WAITING);
    const importResponse = await bbDocsImportDocCreate({ query: { id: data.id } });

    if (importResponse.error) {
      setImportQueryState(QUERY_STATE.FAIL);
      setImportError(importResponse.error);

      await Promise.all([handleRefetch(), dispatch(loadDoc(data.id))]);

      setImportQueryState(QUERY_STATE.FAIL);
      setImportError(importResponse.error);
    } else {
      // refresh
      setImportQueryState(QUERY_STATE.SUCCESS);

      await Promise.all([handleRefetch(), dispatch(loadDoc(data.id))]);
    }
  };

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setValidate(false);
  }, [setValidate, setSaveQueryState]);

  const name = useInput<InputProps>({
    name: 'name',
    isDisabled: !acl.canUpdate,
    initialValue: data.name,
    label: formatMessage(docFormMessages.name),
    validate: validate,
    validator: (value) =>
      !value ? formatMessage(validatorMessages.buildingDescriptionError) : null,
    onChange: formItemChangeCallback,
    ...formItemServerErrors.name,
  });

  const description = useInput<InputProps>({
    name: 'description',
    isDisabled: !acl.canUpdate,
    initialValue: data.description,
    label: formatMessage(docFormMessages.description),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.description,
  });

  const prepareGeodesistItems = useCallback(async (search: string) => {
    const response = await geodesistsList({ query: { search } });
    if (response.ok) {
      return response.data?.results ?? null;
    }
    return null;
  }, []);
  const geodesist = useSelect<DocsRetrieveResponse['geodesist'], DocsRetrieveResponse['geodesist']>(
    {
      name: 'geodesist',
      isDisabled: !acl.canUpdate,
      label: formatMessage(docFormMessages.geodesist),
      initialValue: data.geodesist,
      onChange: formItemChangeCallback,
      prepareItems: prepareGeodesistItems,
      renderValue: (item) => (item.name || item.surname ? `${item.name} ${item.surname}` : ''),
      renderItem: renderUserSelectItem,
      ...formItemServerErrors.geodesist,
    },
  );

  // DTM DOCUMENTATION TYPE
  const dtmDocumentationTypeItems = useSelectMessages(
    dtmDocumentationTypesMessages,
    DtmDataTypeEnum,
  );

  const data_type = useInput({
    name: 'data_type',
    isDisabled: !acl.canUpdate,
    initialValue: data.data_type,
    items: dtmDocumentationTypeItems,
    label: formatMessage(docFormMessages.data_type),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.data_type,
  });

  const state = { value: data.state };
  const prejimka_type = { value: data.prejimka_type };
  const formItems = {
    state,
    prejimka_type,
    name,
    description,
    geodesist,
    data_type,
  } as const;

  // ATTACHMENTS
  const uploadHelpText = formatMessage.fallback(['document.bb.uploadHelpText']);

  // EDITOR
  // - TODO - jak bude konfigurovane ? bude zavisle na data_type ? TI/DI/ZPS ?
  // URL od @vlama ?
  let editorUrl = undefined;
  switch (data_type.value) {
    case DtmDataTypeEnum.TI:
      editorUrl =
        config.editorTI?.url &&
        new URL(`${config.editorTI.url}${docId}`, window.location.href).href;
      break;
    case DtmDataTypeEnum.DI:
      editorUrl =
        config.editorDI?.url &&
        new URL(`${config.editorDI.url}${docId}`, window.location.href).href;
      break;
    case DtmDataTypeEnum.ZPS:
      editorUrl =
        config.editorZPS?.url &&
        new URL(`${config.editorZPS.url}${docId}`, window.location.href).href;
      break;
  }

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div className='dmx-doc--head sg-u-vs-2'>
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, 'bb', {
                    id: data.id || 'new',
                  })}
                </PageTitle>
                {data.state && (
                  <DocBadge
                    docState={data.state}
                    flow={data.flow}
                    rejectedBySubjekty={data.rejected_by_subjekty}
                  />
                )}
              </header>
              <BtnBar>
                {acl.docsUpdate && (
                  <CtaBtn
                    queryState={toBtnQueryState(saveQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSaveDocWithValidation}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSave} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionSubmit && (
                  <CtaBtn
                    queryState={toBtnQueryState(submitQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSubmitBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSubmit} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionAccept && (
                  <CtaBtn
                    queryState={toBtnQueryState(acceptQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleAcceptBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnAccept} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionImport && (
                  <CtaBtn
                    queryState={toBtnQueryState(importQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleImportBtnClick}
                  >
                    {data.state === DOC_STATE.IMPORT_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnBbImport} />
                    )}
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionMapEdit && editorUrl && (
                  <CtaLink href={editorUrl} target='_blank'>
                    <FormattedMessage {...docEditButtonsMsg.docBtnMapEdit} />
                  </CtaLink>
                )}

                {!!docId && acl.docsDestroy && (
                  <SecondaryBtn
                    isDisabled={isActionRunning || removeQueryState === QUERY_STATE.WAITING}
                    isDanger
                    onClick={handleDeleteBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRemove} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionClose && (
                  <CtaBtn
                    queryState={toBtnQueryState(closeQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCloseBtnClick}
                  >
                    {data.state === DOC_STATE.CLOSE_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnClose} />
                    )}
                  </CtaBtn>
                )}
              </BtnBar>
            </div>
            {showLogDownloadError && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.fileDownloadError} />
              </DangerAlert>
            )}
            {(resource.queryState === QUERY_STATE.WAITING ||
              saveQueryState === QUERY_STATE.WAITING ||
              submitQueryState === QUERY_STATE.WAITING ||
              acceptQueryState === QUERY_STATE.WAITING ||
              closeQueryState === QUERY_STATE.WAITING ||
              destroyQueryState === QUERY_STATE.WAITING) && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {(saveQueryState === QUERY_STATE.FAIL ||
              submitQueryState === QUERY_STATE.FAIL ||
              acceptQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateFail} />
              </DangerAlert>
            )}

            {saveQueryState === QUERY_STATE.SUCCESS && (
              <SuccessAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateSuccess} />
              </SuccessAlert>
            )}
            {formItemServerErrors.non_field_errors && (
              <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}
            {closeQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.closeQueryStateFail} />
              </DangerAlert>
            )}
            {destroyQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.destroyQueryStateFail} />
              </DangerAlert>
            )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <Form onSubmit={handleSaveDocWithValidation}>
                <div className='sg-u-vs-4'>
                  <section>
                    <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                    <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                      <FormRow>
                        <FormItem {...name} isRequired>
                          <Input {...name} />
                        </FormItem>
                      </FormRow>
                      <FormRow>
                        <FormItem {...description}>
                          <Textarea {...description} />
                        </FormItem>
                      </FormRow>
                      <FormRow>
                        <FormItem {...geodesist}>
                          <CustomSelect {...geodesist} />
                        </FormItem>
                      </FormRow>
                      <FormRow>
                        <FormItem {...data_type}>
                          <RadioGroup {...data_type} direction='row' />
                        </FormItem>
                      </FormRow>
                    </div>
                  </section>
                </div>
              </Form>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-3'>
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2'>
                      <FormattedMessage {...docFormMessages.attachments_docs} />
                    </h3>
                    <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                      {uploadHelpText && (
                        <div className='sg-a-mw-fc'>
                          <InfoAlert>{uploadHelpText}</InfoAlert>
                        </div>
                      )}
                      <DocAttachments
                        doc={resource?.data}
                        prejimkaType={data.prejimka_type}
                        createDoc={handleSaveDocWithoutValidation}
                        attachments={stateData.attachments}
                        importError={importError}
                        importQueryState={importQueryState}
                      />
                    </div>
                  </section>
                  <section className='sg-a-mt-2'>
                    <DocDownloadFiles
                      title={<FormattedMessage {...docFormMessages.attachments_system} />}
                      docId={docId}
                      items={[
                        {
                          permission: acl.docsActionDownloadCloseLog,
                          link: closeLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                          ext: 'zip',
                        },
                        {
                          permission: acl.docsActionDownloadImportLog,
                          link: importLogDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />,
                          ext: 'zip',
                        },
                      ]}
                    />
                  </section>
                </div>
              </section>
            </div>

            <div className='dmx-doc--rightCol sg-u-vs-4'>
              {acl.docsActionShowLog && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.logs} />
                  </h3>
                  <LogList objectId={docId!} resourceType={'doc'} />
                </section>
              )}
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...docFormMessages.history} />
                </h3>
                <HistoryList history={data.history} />
              </section>
            </div>
          </div>
        </article>
      )}
    </MainContent>
  );
};
